import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const GestoresTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [gestorUsers, setGestorUsers] = useState([]);
  const [showGestorForm, setShowGestorForm] = useState(false);
  const [editingGestorUser, setEditingGestorUser] = useState(null);
  const [gestorForm, setGestorForm] = useState({
    username: "",
    password: "",
    nombre: "",
    email: "",
    puede_gestionar_eventos: false,
    puede_ver_aforo: false,
    puede_escanear: false,
    eventos_asignados: []
  });

  const fetchGestorUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/gestor-users`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setGestorUsers(response.data);
    } catch (e) {
      console.error("Error fetching gestor users:", e);
    }
  }, [credentials]);

  useEffect(() => {
    fetchGestorUsers();
  }, [fetchGestorUsers]);

  return (
    <>
          <>
            {/* Add Gestor User Button */}
            {!showGestorForm && (
              <button 
                onClick={() => {
                  setShowGestorForm(true);
                  setEditingGestorUser(null);
                  setGestorForm({
                    username: "",
                    password: "",
                    nombre: "",
                    email: "",
                    puede_gestionar_eventos: false,
                    puede_ver_aforo: false,
                    puede_escanear: false,
                    eventos_asignados: []
                  });
                }}
                className="btn-primary mb-8 w-auto px-6"
                data-testid="add-gestor-btn"
              >
                <UserPlus size={18} className="inline mr-2" />
                Nuevo Usuario Gestor
              </button>
            )}

            {/* Gestor User Form */}
            {showGestorForm && (
              <div className="form-section mb-8">
                <button 
                  onClick={() => {
                    setShowGestorForm(false);
                    setEditingGestorUser(null);
                  }}
                  className="text-[var(--text-muted)] hover:text-white mb-4 flex items-center gap-2"
                >
                  <ChevronLeft size={18} />
                  Volver
                </button>
                <h3 className="form-title mb-6">{editingGestorUser ? 'Editar Usuario Gestor' : 'Nuevo Usuario Gestor'}</h3>
                
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setError("");
                  try {
                    if (editingGestorUser) {
                      const updateData = {
                        nombre: gestorForm.nombre,
                        email: gestorForm.email,
                        puede_gestionar_eventos: gestorForm.puede_gestionar_eventos,
                        puede_ver_aforo: gestorForm.puede_ver_aforo,
                        puede_escanear: gestorForm.puede_escanear,
                        eventos_asignados: gestorForm.eventos_asignados
                      };
                      if (gestorForm.password) {
                        updateData.password = gestorForm.password;
                      }
                      await axios.put(`${API}/admin/gestor-users/${editingGestorUser.id}`, updateData, {
                        headers: { Authorization: `Basic ${credentials}` }
                      });
                    } else {
                      await axios.post(`${API}/admin/gestor-users`, gestorForm, {
                        headers: { Authorization: `Basic ${credentials}` }
                      });
                    }
                    setShowGestorForm(false);
                    setEditingGestorUser(null);
                    setGestorForm({ username: "", password: "", nombre: "", email: "", puede_gestionar_eventos: false, puede_ver_aforo: false, puede_escanear: false, eventos_asignados: [] });
                    fetchGestorUsers();
                  } catch (err) {
                    setError(err.response?.data?.detail || "Error al guardar usuario");
                  }
                }}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Usuario</label>
                      <input
                        type="text"
                        className="custom-input"
                        value={gestorForm.username}
                        onChange={(e) => setGestorForm(prev => ({ ...prev, username: e.target.value }))}
                        required
                        disabled={!!editingGestorUser}
                        data-testid="gestor-username"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{editingGestorUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}</label>
                      <PasswordInput
                        value={gestorForm.password}
                        onChange={(e) => setGestorForm(prev => ({ ...prev, password: e.target.value }))}
                        required={!editingGestorUser}
                        testId="gestor-password"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre Completo</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={gestorForm.nombre}
                      onChange={(e) => setGestorForm(prev => ({ ...prev, nombre: e.target.value }))}
                      required
                      data-testid="gestor-nombre"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="custom-input"
                      placeholder="email@ejemplo.com"
                      value={gestorForm.email}
                      onChange={(e) => setGestorForm(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="gestor-email"
                    />
                  </div>

                  {/* Privileges */}
                  <div className="form-group">
                    <label className="form-label mb-3">Privilegios</label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--surface)] border border-[var(--border-color)] hover:border-orange-400 transition-colors">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-orange-400"
                          checked={gestorForm.puede_gestionar_eventos}
                          onChange={(e) => setGestorForm(prev => ({ ...prev, puede_gestionar_eventos: e.target.checked }))}
                        />
                        <div>
                          <span className="text-white font-medium">Gestionar Eventos</span>
                          <p className="text-[var(--text-muted)] text-xs">Puede editar los eventos asignados</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--surface)] border border-[var(--border-color)] hover:border-[var(--accent-cyan)] transition-colors">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-[var(--accent-cyan)]"
                          checked={gestorForm.puede_ver_aforo}
                          onChange={(e) => setGestorForm(prev => ({ ...prev, puede_ver_aforo: e.target.checked }))}
                        />
                        <div>
                          <span className="text-white font-medium">Ver Control de Aforo</span>
                          <p className="text-[var(--text-muted)] text-xs">Puede ver estadísticas de los eventos asignados</p>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--surface)] border border-[var(--border-color)] hover:border-[var(--success)] transition-colors">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-[var(--success)]"
                          checked={gestorForm.puede_escanear}
                          onChange={(e) => setGestorForm(prev => ({ ...prev, puede_escanear: e.target.checked }))}
                        />
                        <div>
                          <span className="text-white font-medium">Escanear Entradas</span>
                          <p className="text-[var(--text-muted)] text-xs">Puede validar entradas en los eventos asignados</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Eventos Asignados */}
                  <div className="form-group">
                    <label className="form-label mb-3">Eventos Asignados</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {events.filter(e => e.activo).map(event => (
                        <label 
                          key={event.id}
                          className={`flex items-center gap-3 cursor-pointer p-3 border transition-colors ${
                            gestorForm.eventos_asignados.includes(event.id)
                              ? 'bg-orange-500/20 border-orange-500'
                              : 'bg-[var(--surface)] border-[var(--border-color)] hover:border-orange-400'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="w-5 h-5 accent-orange-400"
                            checked={gestorForm.eventos_asignados.includes(event.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setGestorForm(prev => ({
                                  ...prev,
                                  eventos_asignados: [...prev.eventos_asignados, event.id]
                                }));
                              } else {
                                setGestorForm(prev => ({
                                  ...prev,
                                  eventos_asignados: prev.eventos_asignados.filter(id => id !== event.id)
                                }));
                              }
                            }}
                          />
                          <span className="text-white">{event.nombre}</span>
                          <span className="text-[var(--text-muted)] text-sm">({event.fecha})</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary w-full mt-4"
                    data-testid="save-gestor-btn"
                  >
                    {editingGestorUser ? 'Guardar Cambios' : 'Crear Usuario Gestor'}
                  </button>
                </form>
              </div>
            )}

            {/* Gestor Users List */}
            <div>
              <h3 className="font-heading text-lg text-[var(--text-muted)] uppercase mb-4">
                Usuarios Gestores ({gestorUsers.length})
              </h3>
              {gestorUsers.length === 0 ? (
                <p className="text-[var(--text-muted)] text-center py-8">No hay usuarios gestores</p>
              ) : (
                <div className="grid gap-4">
                  {gestorUsers.map(user => {
                    const eventosNombres = user.eventos_asignados?.map(eventId => {
                      const evento = events.find(e => e.id === eventId);
                      return evento?.nombre || eventId.slice(0, 8);
                    }) || [];
                    
                    return (
                      <div 
                        key={user.id} 
                        className="admin-event-card"
                        data-testid={`gestor-user-${user.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-500 flex items-center justify-center">
                              <UserPlus size={24} className="text-black" />
                            </div>
                            <div>
                              <h4 className="text-white font-bold">{user.nombre}</h4>
                              <p className="text-[var(--text-muted)] text-sm">@{user.username}</p>
                              {user.email && (
                                <p className="text-orange-400 text-xs">📧 {user.email}</p>
                              )}
                              <div className="flex gap-2 mt-1">
                                {user.puede_gestionar_eventos && (
                                  <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs">Eventos</span>
                                )}
                                {user.puede_ver_aforo && (
                                  <span className="px-2 py-0.5 bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)] text-xs">Aforo</span>
                                )}
                                {user.puede_escanear && (
                                  <span className="px-2 py-0.5 bg-[var(--success)]/20 text-[var(--success)] text-xs">Escáner</span>
                                )}
                              </div>
                              {eventosNombres.length > 0 && (
                                <p className="text-[var(--text-muted)] text-xs mt-1">
                                  Eventos: {eventosNombres.join(", ")}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                setEditingGestorUser(user);
                                setGestorForm({
                                  username: user.username,
                                  password: "",
                                  nombre: user.nombre,
                                  email: user.email || "",
                                  puede_gestionar_eventos: user.puede_gestionar_eventos || false,
                                  puede_ver_aforo: user.puede_ver_aforo || false,
                                  puede_escanear: user.puede_escanear || false,
                                  eventos_asignados: user.eventos_asignados || []
                                });
                                setShowGestorForm(true);
                              }}
                              className="p-2 text-[var(--text-muted)] hover:text-orange-400 transition-colors"
                              title="Editar usuario"
                              data-testid={`edit-gestor-${user.id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`¿Eliminar al usuario ${user.nombre}?`)) {
                                  try {
                                    await axios.delete(`${API}/admin/gestor-users/${user.id}`, {
                                      headers: { Authorization: `Basic ${credentials}` }
                                    });
                                    fetchGestorUsers();
                                  } catch (err) {
                                    setError("Error al eliminar usuario");
                                  }
                                }
                              }}
                              className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                              title="Eliminar usuario"
                              data-testid={`delete-gestor-${user.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>

    </>
  );
};

export default GestoresTab;
